import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../../apiBase';
import { SimpleBarChart, SimpleLineChart } from './AdminChartsSvg';
import './AdminDashboard.css';

const API = getApiBaseUrl();

function adminApi(token) {
    return axios.create({
        baseURL: API,
        headers: { Authorization: `Bearer ${token}` },
    });
}

function fmt(n) {
    return (n ?? 0).toLocaleString('ru-RU');
}

function fmtMoney(n) {
    return `${fmt(Math.round(n ?? 0))} ₽`;
}

function Delta({ value, suffix = '' }) {
    if (value == null || value === 0) {
        return <span className="grafana-kpi-meta">—</span>;
    }
    const cls = value > 0 ? 'grafana-delta-positive' : 'grafana-delta-negative';
    const sign = value > 0 ? '↑' : '↓';
    return (
        <span className={`grafana-kpi-meta ${cls}`}>
            {sign}{Math.abs(value)} {suffix}
        </span>
    );
}

function KpiCard({ className, icon, label, value, lines }) {
    return (
        <div className={`grafana-kpi-card ${className || ''}`}>
            <div className="grafana-kpi-label">{icon} {label}</div>
            <div className="grafana-kpi-value">{value}</div>
            {lines.map((line, i) => (
                <div key={i}>{line}</div>
            ))}
        </div>
    );
}

export default function AdminPlatformTab({ token }) {
    const [overview, setOverview] = useState(null);
    const [trend, setTrend] = useState([]);
    const [daily, setDaily] = useState(null);
    const [billing, setBilling] = useState([]);
    const [err, setErr] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;

        async function load() {
            try {
                const api = adminApi(token);
                const today = new Date().toISOString().slice(0, 10);
                const [ov, tr, ds, bill] = await Promise.all([
                    api.get('/admin/reports/platform-overview'),
                    api.get('/admin/reports/users-trend', { params: { days: 30 } }),
                    api.get('/admin/reports/daily-stats', { params: { date: today } }),
                    api.get('/admin/reports/schools-billing', { params: { sortBy: 'students' } }),
                ]);
                if (!cancelled) {
                    setOverview(ov.data);
                    setTrend(tr.data || []);
                    setDaily(ds.data);
                    setBilling(bill.data || []);
                    setErr('');
                }
            } catch (e) {
                if (!cancelled) {
                    setErr(e.response?.data?.message || 'Не удалось загрузить дашборд');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();
        const id = setInterval(load, 60000);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, [token]);

    const trendChartData = useMemo(
        () => trend.map((d) => ({
            ...d,
            label: d.date?.slice(5) || d.date,
        })),
        [trend]
    );

    const statusChartData = useMemo(() => {
        const counts = { active: 0, trial: 0, suspended: 0 };
        for (const row of billing) {
            const s = row.subscriptionStatus || 'unknown';
            if (s in counts) counts[s] += 1;
        }
        return [
            { status: 'Active', count: counts.active, fill: '#48bb78' },
            { status: 'Trial', count: counts.trial, fill: '#f59e0b' },
            { status: 'Suspended', count: counts.suspended, fill: '#6b7280' },
        ];
    }, [billing]);

    const topSchools = useMemo(
        () => [...billing]
            .sort((a, b) => (b.currentActiveStudents || 0) - (a.currentActiveStudents || 0))
            .slice(0, 5),
        [billing]
    );

    if (loading && !overview) {
        return <div className="grafana-dashboard grafana-loading">Загрузка дашборда…</div>;
    }

    return (
        <div className="grafana-dashboard">
            {err && <div className="grafana-error">{err}</div>}

            {overview && (
                <div className="grafana-kpi-grid">
                    <KpiCard
                        label="Пользователи"
                        icon="👥"
                        value={fmt(overview.totalUsers)}
                        lines={[
                            <Delta key="d" value={overview.newUsersToday} suffix="сегодня" />,
                            <span key="w" className="grafana-kpi-meta grafana-delta-positive">
                                ↑{fmt(overview.newUsersThisWeek)} за неделю
                            </span>,
                        ]}
                    />
                    <KpiCard
                        className="grafana-kpi-card--schools"
                        label="Школы"
                        icon="🏫"
                        value={fmt(overview.totalSchools)}
                        lines={[
                            <Delta key="d" value={overview.newSchoolsToday} suffix="сегодня" />,
                            <span key="m" className="grafana-kpi-meta grafana-delta-positive">
                                ↑{fmt(overview.newSchoolsThisMonth)} за месяц
                            </span>,
                        ]}
                    />
                    <KpiCard
                        className="grafana-kpi-card--subs"
                        label="Подписки"
                        icon="💎"
                        value={fmt(overview.activeSubscriptions)}
                        lines={[
                            <span key="p" className="grafana-kpi-meta">{fmt(overview.paidSubscriptions)} платных</span>,
                            <span key="t" className="grafana-kpi-meta">{fmt(overview.trialSubscriptions)} trial</span>,
                        ]}
                    />
                    <KpiCard
                        className="grafana-kpi-card--mrr"
                        label="MRR"
                        icon="💰"
                        value={fmtMoney(overview.totalMrr)}
                        lines={[
                            <span key="arr" className="grafana-kpi-meta">
                                ARR ~{fmtMoney(overview.totalArrEstimate)}
                            </span>,
                        ]}
                    />
                </div>
            )}

            <div className="grafana-charts-row">
                <div className="grafana-panel" style={{ marginBottom: 0 }}>
                    <div className="grafana-panel-title">Новые пользователи — 30 дней</div>
                    <SimpleLineChart
                        data={trendChartData}
                        dataKey="newUsers"
                        labelKey="label"
                    />
                </div>

                <div className="grafana-panel" style={{ marginBottom: 0 }}>
                    <div className="grafana-panel-title">Школы по статусу подписки</div>
                    <SimpleBarChart data={statusChartData} />
                </div>
            </div>

            <div className="grafana-tables-row">
                <div className="grafana-panel" style={{ marginBottom: 0 }}>
                    <div className="grafana-panel-title">Последние регистрации сегодня</div>
                    <table className="grafana-table">
                        <thead>
                            <tr>
                                <th>Время</th>
                                <th>Имя</th>
                                <th>Контакт</th>
                                <th>Роль</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(daily?.newUsers || []).slice(0, 10).map((u) => (
                                <tr key={u.id}>
                                    <td>{new Date(u.createdAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</td>
                                    <td>{u.username}</td>
                                    <td>{u.phone || u.email || '—'}</td>
                                    <td>{u.role}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {(daily?.newUsers || []).length === 0 && (
                        <div className="grafana-empty">Сегодня регистраций пока нет</div>
                    )}
                </div>

                <div className="grafana-panel" style={{ marginBottom: 0 }}>
                    <div className="grafana-panel-title">Топ 5 школ по активным ученикам</div>
                    <table className="grafana-table">
                        <thead>
                            <tr>
                                <th>Школа</th>
                                <th>Активных</th>
                                <th>Тариф</th>
                                <th>Итого/мес</th>
                            </tr>
                        </thead>
                        <tbody>
                            {topSchools.map((s) => (
                                <tr key={s.schoolId}>
                                    <td>{s.schoolName}</td>
                                    <td>{s.currentActiveStudents}</td>
                                    <td>{s.planName}</td>
                                    <td>{fmtMoney(s.estimatedTotal)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {topSchools.length === 0 && (
                        <div className="grafana-empty">Нет данных по школам</div>
                    )}
                </div>
            </div>
        </div>
    );
}
